// DataUpahTukang.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, CircularProgress, Alert,
  Snackbar, Fade, Button, Stack, Grid, useMediaQuery,
  TextField, InputAdornment, IconButton, Paper, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Avatar, LinearProgress, MenuItem, FormControl, InputLabel, Select
} from '@mui/material';
import {
  Refresh, Add, Visibility, Edit, Delete, Search,
  NavigateBefore, NavigateNext, Person, AttachMoney,
  CalendarToday, Receipt, Image as ImageIcon, Clear
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  getAllUpahTukang,
  getUpahTukangByBantuan,
  deleteUpahTukang,
  getFotoKwitansiUrl,
  getUpahTukangById
} from '../API/upahTukang';
import { getBantuan } from '../API/bantuan';
import ModalAddUpahTukang from './ModalAddUpahTukang';
import { ModalDetailUpahTukang } from './ModalDetailUpahTukang';
import ModalEditUpahTukang from './ModalEditUpahTukang';

const formatRp = (n) => {
  if (!n && n !== 0) return 'Rp 0';
  return 'Rp ' + Number(n).toLocaleString('id-ID');
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const DataUpahTukang = () => {
  const user = useSelector((state) => state.authAdmin?.user);
  const userRole = user?.role || '';
  const isAdmin = userRole === 'admin';
  const isTFL = userRole === 'TFL';
  const isAGR = userRole === 'AGR';

  const isMobile = useMediaQuery('(max-width:800px)');

  // State
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBantuanId, setFilterBantuanId] = useState('');
  const [bantuanOptions, setBantuanOptions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Modal state
  const [addOpen, setAddOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedBantuan, setSelectedBantuan] = useState(null);

  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // Fetch bantuan options for filter
  const fetchBantuanOptions = useCallback(async () => {
    try {
      const res = await getBantuan({ page: 1, limit: 100 });
      const options = (res.data.data || []).map((b) => ({
        id: b.id,
        label: `#${b.id} - ${b.pemohon?.nama || b.namaLengkap || 'Tidak Diketahui'} (${b.JenisBantuan || '-'})`
      }));
      setBantuanOptions(options);
    } catch (error) {
      console.error('Error fetching bantuan options:', error);
    }
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        search: searchTerm,
        bantuanId: filterBantuanId || undefined
      };
      const res = await getAllUpahTukang(params);
      setRows(res.data || []);
      setTotalItems(res.pagination?.totalItems || 0);
      setTotalPages(res.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching upah tukang:', error);
      notify('Gagal mengambil data upah tukang', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm, filterBantuanId]);

  useEffect(() => {
    fetchData();
    fetchBantuanOptions();
  }, [fetchData, fetchBantuanOptions]);

  // Handle delete
  const handleDelete = (item) => {
    Swal.fire({
      title: 'Hapus Data Upah Tukang?',
      text: `Yakin ingin menghapus data upah tukang ini?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteUpahTukang(item.id);
          notify('Data upah tukang berhasil dihapus');
          fetchData();
        } catch (error) {
          notify('Gagal menghapus data', 'error');
        }
      }
    });
  };

  // Handle view detail
  const handleViewDetail = (item) => {
    setSelectedItem(item);
    setDetailOpen(true);
  };

  // Handle edit
  const handleEdit = (item) => {
    setSelectedItem(item);
    setEditOpen(true);
  };

  const contentStyle = isMobile ? {} : { marginLeft: "250px", marginRight: "20px" };

  // Check if user can edit/delete
  const canEdit = isAdmin || isTFL || isAGR;
  const canDelete = isAdmin;

  return (
    <div className="table-responsive rounded border">
      <div className="main-content" style={contentStyle}>
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person color="primary" sx={{ fontSize: 32 }} />
              <Typography variant="h5" fontWeight={700}>Data Upah Tukang</Typography>
              <Chip 
                size="small" 
                label={userRole || 'Guest'} 
                color={isAdmin ? 'error' : isTFL ? 'warning' : isAGR ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {canEdit && <Button 
                variant="contained" 
                color="success" 
                startIcon={<Add />} 
                size="small"
                onClick={() => setAddOpen(true)}
              >
                Tambah Upah Tukang
              </Button>}
              <Button 
                variant="outlined" 
                startIcon={<Refresh />} 
                size="small" 
                onClick={fetchData}
              >
                Muat Ulang
              </Button>
            </Box>
          </Box>

          {/* Filter */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  size="small"
                  placeholder="Cari nama bantuan..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Filter Bantuan</InputLabel>
                  <Select
                    value={filterBantuanId}
                    onChange={(e) => setFilterBantuanId(e.target.value)}
                    label="Filter Bantuan"
                  >
                    <MenuItem value="">Semua Bantuan</MenuItem>
                    {bantuanOptions.map((b) => (
                      <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Button 
                  variant="outlined" 
                  startIcon={<Clear />}
                  onClick={() => {
                    setSearchTerm('');
                    setFilterBantuanId('');
                    setPage(1);
                  }}
                  fullWidth
                >
                  Reset Filter
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Table */}
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
             

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : rows.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography color="text.secondary">Belum ada data upah tukang</Typography>
                </Box>
              ) : (
                <TableContainer>
<Table>
                    <TableHead sx={{ bgcolor: '#f5f7fa' }}>
                      <TableRow>
                        <TableCell>No.</TableCell>
                        <TableCell>Bantuan</TableCell>
                        <TableCell align="center">Tahap</TableCell>
                        <TableCell align="right">Nominal</TableCell>
                        <TableCell>Tanggal</TableCell>
                        <TableCell align="center">Aksi</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((item, index) => (
                        <TableRow key={item.id} hover>
                          <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              #{item.bantuanId}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.Bantuan?.JenisBantuan || '-'} - {item.Bantuan?.Questionnaire?.namaLengkapKK || item.Bantuan?.Questionnaire?.kecamatan || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={item.tahap === 'tahap_1' ? 'Tahap I' : 'Tahap II'}
                              color={item.tahap === 'tahap_1' ? 'warning' : 'success'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} color="primary.main">
                              {formatRp(item.totalUpah)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(item.createdAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ minWidth: 120, whiteSpace: 'nowrap' }}>
  <Stack 
    direction="row" 
    spacing={0.5} 
    justifyContent="center" 
    alignItems="center"
    sx={{
      '& .MuiIconButton-root': {
        padding: '4px',
        width: '32px',
        height: '32px',
        borderRadius: '8px'
      },
      '& .MuiIconButton-sizeSmall': {
        width: '28px',
        height: '28px'
      }
    }}
  >
    <Tooltip title="Detail">
      <IconButton 
        size="small" 
        sx={{ 
          color: '#1976d2',
          backgroundColor: '#e3f2fd',
          '&:hover': { backgroundColor: '#bbdefb' }
        }}
        onClick={() => handleViewDetail(item)}
      >
        <Visibility fontSize="small" />
      </IconButton>
    </Tooltip>
    
    {canEdit && (
      <Tooltip title="Edit">
        <IconButton 
          size="small" 
          sx={{ 
            color: '#ed6c02',
            backgroundColor: '#fff3e0',
            '&:hover': { backgroundColor: '#ffe0b2' }
          }}
          onClick={() => handleEdit(item)}
        >
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
    
    {canDelete && (
      <Tooltip title="Hapus">
        <IconButton 
          size="small" 
          sx={{ 
            color: '#d32f2f',
            backgroundColor: '#ffebee',
            '&:hover': { backgroundColor: '#ffcdd2' }
          }}
          onClick={() => handleDelete(item)}
        >
          <Delete fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Stack>
</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Pagination */}
              {rows.length > 0 && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mt: 2, 
                  flexWrap: 'wrap', 
                  gap: 1 
                }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={limit}
                        onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                      >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary">
                      Hal. {page} dari {totalPages} • {totalItems} data
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<NavigateBefore />}
                      disabled={page <= 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                      Prev
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      endIcon={<NavigateNext />}
                      disabled={page >= totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </Button>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Modals */}
          <ModalAddUpahTukang
            open={addOpen}
            onClose={() => setAddOpen(false)}
            onSuccess={fetchData}
          />

          <ModalDetailUpahTukang
            open={detailOpen}
            onClose={() => setDetailOpen(false)}
            data={selectedItem}
          />

          <ModalEditUpahTukang
            open={editOpen}
            onClose={() => setEditOpen(false)}
            data={selectedItem}
            onSuccess={fetchData}
          />

          {/* Snackbar */}
          <Snackbar 
            open={snackbar.open} 
            autoHideDuration={4000}
            onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            TransitionComponent={Fade}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert 
              onClose={() => setSnackbar(s => ({ ...s, open: false }))}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </div>
    </div>
  );
};

export default DataUpahTukang;
